import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';

const CityCheckboxFilter = ({
  placeholder = 'Search...',
  options = [],
  selectedValues = [],
  onApply = () => {},
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelected, setTempSelected] = useState(selectedValues || []);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 280 });

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Sync tempSelected with selectedValues when selectedValues changes externally
  useEffect(() => {
    if (!isOpen) {
      setTempSelected(selectedValues || []);
    }
  }, [selectedValues, isOpen]);

  // Update position relative to trigger button
  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownWidth = 280;
    let { left } = rect;
    if (left + dropdownWidth > window.innerWidth - 10) {
      left = window.innerWidth - dropdownWidth - 10;
    }
    setCoords({
      top: rect.bottom + 4,
      left: Math.max(10, left),
      width: dropdownWidth,
    });
  };

  const handleToggleOpen = () => {
    if (isLoading) return;
    if (!isOpen) {
      setTempSelected(selectedValues || []);
      setSearchTerm('');
      updatePosition();
      setIsOpen(true);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    } else {
      setIsOpen(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setTempSelected(selectedValues || []);
      }
    };

    const handleScrollOrResize = (e) => {
      // If scrolling inside dropdown, don't close
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      updatePosition();
    };

    document.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, selectedValues]);

  // Toggle item selection
  const handleItemToggle = (option) => {
    setTempSelected((prev) => {
      const exists = prev.some((item) => String(item.value) === String(option.value));
      if (exists) {
        return prev.filter((item) => String(item.value) !== String(option.value));
      }
      return [...prev, option];
    });
  };

  // Reset selection
  const handleReset = () => {
    setTempSelected([]);
  };

  // Apply selection
  const handleApply = () => {
    onApply(tempSelected);
    setIsOpen(false);
  };

  // Quick clear directly from trigger
  const handleQuickClear = (e) => {
    e.stopPropagation();
    setTempSelected([]);
    onApply([]);
  };

  // Group into selected and unselected so selected items ALWAYS appear at the top
  const { selectedList, unselectedList } = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const selectedMap = new Map(tempSelected.map((item) => [String(item.value), item]));

    const sel = [];
    const unsel = [];

    (options || []).forEach((opt) => {
      const isSel = selectedMap.has(String(opt.value));
      const matchesSearch =
        !query ||
        (opt.label && opt.label.toLowerCase().includes(query)) ||
        (opt.cityName && opt.cityName.toLowerCase().includes(query));

      if (matchesSearch) {
        if (isSel) {
          sel.push(opt);
        } else {
          unsel.push(opt);
        }
      }
    });

    return { selectedList: sel, unselectedList: unsel };
  }, [options, tempSelected, searchTerm]);

  const selectedCount = (selectedValues || []).length;

  return (
    <div className="city-checkbox-filter-container">
      {/* Trigger Button */}
      <div
        ref={triggerRef}
        className={`city-checkbox-filter-trigger ${isOpen ? 'is-open' : ''} ${selectedCount > 0 ? 'has-value' : ''}`}
        onClick={handleToggleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggleOpen();
          }
        }}
      >
        <div className="city-checkbox-filter-trigger-content">
          {selectedCount === 0 ? (
            <span className="city-checkbox-filter-placeholder">{placeholder}</span>
          ) : selectedCount === 1 ? (
            <span className="city-checkbox-filter-single-label" title={selectedValues[0]?.label}>
              {selectedValues[0]?.cityName || selectedValues[0]?.label}
            </span>
          ) : (
            <span
              className="city-checkbox-filter-badge"
              title={selectedValues.map((v) => v.cityName || v.label).join(', ')}
            >
              {selectedCount} selected
            </span>
          )}
        </div>

        <div className="city-checkbox-filter-trigger-icons">
          {selectedCount > 0 && (
            <button
              type="button"
              className="city-checkbox-filter-clear-btn"
              onClick={handleQuickClear}
              title="Clear filter"
              aria-label="Clear filter"
            >
              ×
            </button>
          )}
          <svg
            className={`city-checkbox-filter-chevron ${isOpen ? 'rotate' : ''}`}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {/* Popover Dropdown (Portal) */}
      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            className="city-checkbox-filter-popover"
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              width: `${coords.width}px`,
            }}
          >
            {/* Search Box at Top */}
            <div className="city-checkbox-filter-search-wrap">
              <div className="city-checkbox-filter-search-inner">
                <svg
                  className="city-checkbox-filter-search-icon"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#98a2b3"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="city-checkbox-filter-search-input"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="city-checkbox-filter-search-clear"
                    onClick={() => setSearchTerm('')}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* Checkbox Options List */}
            <div className="city-checkbox-filter-list">
              {selectedList.length === 0 && unselectedList.length === 0 ? (
                <div className="city-checkbox-filter-empty">No cities found</div>
              ) : (
                <>
                  {/* Selected items displayed at top */}
                  {selectedList.length > 0 && (
                    <div className="city-checkbox-filter-section">
                      <div className="city-checkbox-filter-section-title">
                        Selected ({selectedList.length})
                      </div>
                      {selectedList.map((option) => (
                        <label
                          key={`selected-${option.value}`}
                          className="city-checkbox-filter-item is-selected"
                        >
                          <input
                            type="checkbox"
                            className="city-checkbox-filter-checkbox"
                            checked
                            onChange={() => handleItemToggle(option)}
                          />
                          <span className="city-checkbox-filter-item-label" title={option.label}>
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Divider between selected and unselected */}
                  {selectedList.length > 0 && unselectedList.length > 0 && (
                    <div className="city-checkbox-filter-divider" />
                  )}

                  {/* Unselected items */}
                  {unselectedList.length > 0 && (
                    <div className="city-checkbox-filter-section">
                      {selectedList.length > 0 && (
                        <div className="city-checkbox-filter-section-title">All Cities</div>
                      )}
                      {unselectedList.map((option) => (
                        <label key={option.value} className="city-checkbox-filter-item">
                          <input
                            type="checkbox"
                            className="city-checkbox-filter-checkbox"
                            checked={false}
                            onChange={() => handleItemToggle(option)}
                          />
                          <span className="city-checkbox-filter-item-label" title={option.label}>
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer with Reset and Apply buttons */}
            <div className="city-checkbox-filter-footer">
              <button
                type="button"
                className="city-checkbox-filter-btn-reset"
                onClick={handleReset}
              >
                Reset
              </button>
              <button
                type="button"
                className="city-checkbox-filter-btn-apply"
                onClick={handleApply}
              >
                Apply
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CityCheckboxFilter;
